import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Calendar,
  ChevronRight,
  Loader2,
  AlertCircle,
  FolderPlus,
  Users,
  Camera,
  BookOpen,
  Receipt,
  NotebookPen,
  ShoppingBag,
  HardHat,
  MapPin,
} from 'lucide-react';
import { PortalNotifications } from '../../components/portal/PortalNotifications';
import { jobCode, isConstruction } from '../../utils/jobCode';
import { toast } from 'sonner';
import { format } from 'date-fns';

export function PortalHome() {
  const { api, user, features } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [todayJobs, setTodayJobs] = useState([]);
  const [bcEnabled, setBcEnabled] = useState(false);
  const [bcCanSend, setBcCanSend] = useState(false);

  const isAdmin = user?.user_type === 'admin' || user?.roles?.includes('admin');
  const isSurveyor = user?.user_type === 'surveyor' || user?.roles?.includes('surveyor');
  const isSubContractor = user?.user_type === 'sub_contractor';
  const canOrder = features?.purchase_orders && !isSubContractor && (isAdmin || isSurveyor || !!user?.capture_enabled);
  // "Team role" = anyone allowed to see the whole team (admin OR surveyor)
  const isTeamRole = isAdmin || isSurveyor;

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const r = await api.get(`/portal/my-schedule?start_date=${today}&end_date=${today}`);
      setTodayJobs(r.data || []);
      try { const bc = await api.get('/bc/me'); setBcEnabled(!!bc.data?.bc_capture_enabled); setBcCanSend(!!bc.data?.can_send); } catch { /* non-fatal */ }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#F5A623]" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-5 max-w-lg mx-auto pb-24" data-testid="portal-home">
      {/* Greeting */}
      <div className="pt-2">
        <h1 className="text-2xl font-bold text-white">Hello, {user?.name?.split(' ')[0]}!</h1>
        <p className="text-gray-400 text-base mt-1">
          {format(new Date(), 'EEEE, d MMMM yyyy')}
        </p>
      </div>

      {/* Hours change outcomes etc. — unread only */}
      <PortalNotifications compact />

      {/* Today's jobs — operatives & sub-contractors (tap → Job Detail) */}
      {!isTeamRole && (
        <section className="pt-1" data-testid="home-today">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-white font-semibold">Today</h2>
            <button type="button" onClick={() => navigate('/portal/schedule')} className="text-xs text-[#F5A623] flex items-center gap-0.5" data-testid="home-today-schedule-link">Full schedule <ChevronRight className="w-3.5 h-3.5" /></button>
          </div>
          {todayJobs.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/15 p-4 text-sm text-gray-400 text-center" data-testid="home-today-empty">Nothing scheduled for you today.</div>
          ) : (
            <div className="space-y-2">
              {todayJobs.map(job => {
                const con = isConstruction(job);
                return (
                  <button key={job.id} type="button" onClick={() => navigate(`/portal/job/${job.project_id}?date=${job.date}`)} className="w-full text-left rounded-xl bg-[#2A3240] border border-[#F5A623]/30 p-4 flex items-center gap-3 active:scale-[0.99]" data-testid={`home-today-job-${job.id}`}>
                    <span className={`font-black text-base shrink-0 ${con ? 'text-blue-400' : 'text-[#F5A623]'}`}>{jobCode(job, features)}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-white font-semibold truncate">{(job.project_ref || '').split('-').slice(2).join('-') || job.client_name}</span>
                      <span className="flex items-center gap-1 text-xs text-gray-400 truncate"><MapPin className="w-3 h-3 shrink-0" />{[job.address, job.postcode].filter(Boolean).join(', ') || 'Address not set'}</span>
                    </span>
                    <ChevronRight className="w-5 h-5 text-gray-500 shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* ==== SURVEYOR HUB — Site Diary hero + Schedule / Capture tiles ====== */}
      {isSurveyor && (
        <div className="space-y-4" data-testid="surveyor-hub">
          <Card
            className="bg-[#2A3240] border-white/10 border-l-4 border-l-[#F5A623] cursor-pointer hover:bg-[#333d4d] active:scale-[0.98] transition-all"
            style={{ boxShadow: '-7px 0 22px -12px rgba(245,166,35,0.65)' }}
            onClick={() => navigate('/portal/diary')}
            data-testid="hub-site-diary"
          >
            <CardContent className="p-7 text-center">
              <BookOpen className="w-16 h-16 mx-auto text-[#F5A623] mb-3" />
              <h2 className="text-white font-bold text-xl">Site Diary</h2>
              <p className="text-gray-400 text-sm mt-1">Daily logs, labour, materials, weather</p>
            </CardContent>
          </Card>
          <div className="grid grid-cols-2 gap-4">
            <Card
              className="bg-[#2A3240] border-white/10 border-l-4 border-l-purple-400 cursor-pointer hover:bg-[#333d4d] active:scale-[0.98] transition-all"
              style={{ boxShadow: '-7px 0 22px -12px rgba(192,132,252,0.65)' }}
              onClick={() => navigate('/portal/team-schedule')}
              data-testid="hub-ops-portal"
            >
              <CardContent className="p-5 text-center">
                <Users className="w-10 h-10 mx-auto text-purple-400 mb-2" />
                <p className="text-white font-semibold text-sm">Schedule</p>
                <p className="text-gray-400 text-xs mt-0.5">Team schedule</p>
              </CardContent>
            </Card>
            {user?.capture_enabled && (
              <Card
                className="bg-[#2A3240] border-white/10 border-l-4 border-l-[#F5A623] cursor-pointer hover:bg-[#333d4d] active:scale-[0.98] transition-all"
                style={{ boxShadow: '-7px 0 22px -12px rgba(245,166,35,0.6)' }}
                onClick={() => navigate('/portal/capture')}
                data-testid="capture-hero"
              >
                <CardContent className="p-5 text-center">
                  <Camera className="w-10 h-10 mx-auto text-[#F5A623] mb-2" />
                  <p className="text-white font-semibold text-sm">Capture</p>
                  <p className="text-gray-400 text-xs mt-0.5">Receipts &amp; invoices</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* ==== ADMIN / OPERATIVE MY SCHEDULE HERO ============================= */}
      {!isSurveyor && (
        <Card
          className={`bg-[#2A3240] border-white/10 border-l-4 ${isAdmin ? 'border-l-purple-400' : 'border-l-blue-400'}`}
          style={{ boxShadow: `-7px 0 22px -12px ${isAdmin ? 'rgba(192,132,252,0.6)' : 'rgba(96,165,250,0.55)'}` }}
          data-testid="schedule-hero"
        >
          <CardContent className="p-6 text-center">
            {isAdmin ? (
              <>
                <Users className="w-12 h-12 mx-auto text-purple-400 mb-3" />
                <h2 className="text-white font-bold text-lg">Team Schedule</h2>
                <p className="text-gray-400 text-sm mt-1">View and manage team assignments</p>
              </>
            ) : (
              <>
                <Calendar className="w-12 h-12 mx-auto text-blue-400 mb-3" />
                <h2 className="text-white font-bold text-lg">My Schedule</h2>
                <p className="text-gray-400 text-sm mt-1">View your assigned jobs</p>
              </>
            )}
            <Button
              variant="outline"
              onClick={() => navigate(isAdmin ? '/portal/team-schedule' : '/portal/schedule')}
              className="mt-4 h-12 px-6 border-white/20 text-gray-300 active:scale-[0.98] transition-transform"
              data-testid="schedule-hero-btn"
            >
              View Schedule
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Capture tile — only shown when capture is switched on for this user
          (switched-off features are hidden, not greyed out).
          - capture_enabled: false → nothing rendered
            an explanatory message so an operative isn't left staring at a
            missing feature ("silent failure" turns into "self-service
            question")
          - sub_contractor → tile entirely absent, per L-1 Q5 decision
          Surveyors get the compact Capture tile inside the hub above. */}
      {!isSurveyor && user?.capture_enabled ? (
        <Card
          className="bg-[#2A3240] border-l-4 border-l-[#F5A623] border-white/10 my-1.5"
          style={{ boxShadow: '-7px 0 22px -12px rgba(245,166,35,0.6)' }}
          data-testid="capture-hero"
        >
          <CardContent className="p-6 text-center">
            <Camera className="w-12 h-12 mx-auto text-[#F5A623] mb-3" />
            <h2 className="text-white font-bold text-lg">Capture</h2>
            <p className="text-gray-400 text-sm mt-1">Photograph receipts and invoices</p>
            <Button
              variant="outline"
              onClick={() => navigate('/portal/capture')}
              className="mt-4 h-12 px-6 border-white/20 text-gray-300 active:scale-[0.98] transition-transform"
              data-testid="capture-hero-btn"
            >
              Capture Receipt
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {/* Sub-contractor: Invoice quick tile */}
      {isSubContractor && (
        <Card
          className="bg-[#2A3240] border-l-4 border-l-emerald-500/60 border-white/10 cursor-pointer hover:bg-[#333d4d] active:scale-[0.98] transition-all"
          style={{ boxShadow: '-7px 0 22px -12px rgba(52,211,153,0.55)' }}
          onClick={() => navigate('/portal/invoice')}
          data-testid="invoice-hero"
        >
          <CardContent className="p-6 text-center">
            <Receipt className="w-12 h-12 mx-auto text-emerald-400 mb-3" />
            <h2 className="text-white font-bold text-lg">Invoices</h2>
            <p className="text-gray-400 text-sm mt-1">Raise an invoice from your schedule — any period</p>
          </CardContent>
        </Card>
      )}

      {/* Purchase orders — feature-gated (Settings → Features) */}
      {canOrder && (
        <Card
          className="bg-[#2A3240] border-l-4 border-l-amber-300 border-white/10 cursor-pointer hover:bg-[#333d4d] active:scale-[0.98] transition-all"
          style={{ boxShadow: '-7px 0 22px -12px rgba(252,211,77,0.5)' }}
          onClick={() => navigate('/portal/orders')}
          data-testid="orders-hero"
        >
          <CardContent className="p-6 text-center">
            <ShoppingBag className="w-12 h-12 mx-auto text-amber-300 mb-3" />
            <h2 className="text-white font-bold text-lg">Purchase orders</h2>
            <p className="text-gray-400 text-sm mt-1">Get an order number to quote at the merchant, then record what you collected</p>
            <Button
              variant="outline"
              onClick={(e) => { e.stopPropagation(); navigate('/portal/orders/new'); }}
              className="mt-4 h-12 px-6 border-white/20 text-gray-300 active:scale-[0.98] transition-transform"
              data-testid="orders-hero-new"
            >
              Get order number
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Building Control — Save-only entry point for operatives/sub-contractors.
          Surveyors & admins capture it inside the Site Diary day view instead. */}
      {bcEnabled && !bcCanSend && (
        <Card
          className="bg-[#2A3240] border-l-4 border-l-[#F5A623] border-white/10 cursor-pointer hover:bg-[#333d4d] active:scale-[0.98] transition-all"
          style={{ boxShadow: '-7px 0 22px -12px rgba(245,166,35,0.55)' }}
          onClick={() => navigate('/portal/building-control')}
          data-testid="building-control-hero"
        >
          <CardContent className="p-6 text-center">
            <HardHat className="w-12 h-12 mx-auto text-[#F5A623] mb-3" />
            <h2 className="text-white font-bold text-lg">Building Control</h2>
            <p className="text-gray-400 text-sm mt-1">Date, time &amp; GPS-stamped inspection photos — saved against a project</p>
          </CardContent>
        </Card>
      )}

      {/* Site notes — note + photos against a project (everyone in the field) */}
      <Card
        className="bg-[#2A3240] border-l-4 border-l-sky-400 border-white/10 cursor-pointer hover:bg-[#333d4d] active:scale-[0.98] transition-all"
        style={{ boxShadow: '-7px 0 22px -12px rgba(56,189,248,0.55)' }}
        onClick={() => navigate('/portal/site-notes/new')}
        data-testid="site-notes-hero"
      >
        <CardContent className="p-6 text-center">
          <NotebookPen className="w-12 h-12 mx-auto text-sky-400 mb-3" />
          <h2 className="text-white font-bold text-lg">Site notes & photos</h2>
          <p className="text-gray-400 text-sm mt-1">Snap progress photos and leave a note on a project</p>
          <div className="mt-4 flex gap-2 justify-center">
            <Button
              variant="outline"
              onClick={(e) => { e.stopPropagation(); navigate('/portal/site-notes/new'); }}
              className="h-12 px-5 border-white/20 text-gray-300 active:scale-[0.98] transition-transform"
              data-testid="site-notes-hero-add"
            >
              <Camera className="w-4 h-4 mr-2" /> Add note
            </Button>
            <Button
              variant="ghost"
              onClick={(e) => { e.stopPropagation(); navigate('/portal/site-notes'); }}
              className="h-12 px-4 text-gray-400 hover:text-white active:scale-[0.98] transition-transform"
              data-testid="site-notes-hero-list"
            >
              My notes
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions row: Admin + Surveyor keep New Survey / New Project.
          Operatives and sub-contractors do NOT see these (OA-1 §Tier 3). */}
      {isTeamRole && (
        <div className="grid grid-cols-2 gap-5">
          <Card
            className="bg-[#2A3240] border-white/10 border-l-4 border-l-[#F5A623] cursor-pointer hover:bg-[#333d4d] active:bg-[#3a4555] active:scale-[0.98] transition-all"
            style={{ boxShadow: '-7px 0 22px -12px rgba(245,166,35,0.6)' }}
            onClick={() => navigate('/portal/surveys')}
            data-testid="quick-new-survey"
          >
            <CardContent className="p-5 text-center">
              <AlertCircle className="w-10 h-10 mx-auto text-[#F5A623] mb-2" />
              <p className="text-white font-semibold text-sm">New Survey</p>
            </CardContent>
          </Card>
          <Card
            className="bg-[#2A3240] border-white/10 border-l-4 border-l-green-400 cursor-pointer hover:bg-[#333d4d] active:bg-[#3a4555] active:scale-[0.98] transition-all"
            style={{ boxShadow: '-7px 0 22px -12px rgba(74,222,128,0.55)' }}
            onClick={() => navigate('/portal/new-project')}
            data-testid="quick-new-project"
          >
            <CardContent className="p-5 text-center">
              <FolderPlus className="w-10 h-10 mx-auto text-green-400 mb-2" />
              <p className="text-white font-semibold text-sm">New Project</p>
            </CardContent>
          </Card>
        </div>
      )}

    </div>
  );
}
